// Shared chats (collaboration v1): fork, the owner's share warning, and
// suggested follow-ups.
import { companyRequest } from '@/company/api';

export interface ForkedThread {
  id: string;
  title: string;
  updatedAt: number;
  messageCount: number;
  forkedFrom: { threadId: string; messageId: string | null };
}

export interface PrivateConnection {
  connectionId: string;
  provider: string | null;
  name: string | null;
  accountLabel: string | null;
  /** Whose account it is: the chat's owner (own), or — in a fork — the
   * colleague whose turn used it. */
  ownerUserId?: string | null;
  ownerName?: string | null;
  own?: boolean;
}

export interface FollowUpSuggestion {
  id: string;
  threadId: string;
  text: string;
  status: 'open' | 'asked' | 'dismissed';
  createdAt: string;
  suggestedBy: { userId: string; name: string | null };
}

export interface FollowUpList {
  suggestions: FollowUpSuggestion[];
  owner: boolean;
  limit: number;
}

const thread = (id: string) => `/api/v1/threads/${encodeURIComponent(id)}`;

export const chatCollaborationApi = {
  /** A copy of the chat, as the signed-in person's own Personal chat. */
  fork: (threadId: string, messageId?: string) =>
    companyRequest<{ thread: ForkedThread }>(
      `${thread(threadId)}/fork`,
      'POST',
      messageId ? { messageId } : {},
    ),
  /** The owner's private connections this chat used (the Share warning). */
  sharingCheck: (threadId: string) =>
    companyRequest<{ privateConnections: PrivateConnection[] }>(
      `${thread(threadId)}/sharing-check`,
    ),
  followUps: (threadId: string) => companyRequest<FollowUpList>(`${thread(threadId)}/follow-ups`),
  suggest: (threadId: string, text: string) =>
    companyRequest<{ suggestion: FollowUpSuggestion }>(`${thread(threadId)}/follow-ups`, 'POST', {
      text,
    }),
  dismiss: (threadId: string, suggestionId: string) =>
    companyRequest<{ status: 'dismissed' }>(
      `${thread(threadId)}/follow-ups/${encodeURIComponent(suggestionId)}/dismiss`,
      'POST',
      {},
    ),
};

/** The Share warning's two parts: the owner's own private accounts, and
 * colleagues' accounts whose results a fork copied ("Anna’s Gmail"). */
export function sharingWarning(connections: PrivateConnection[]) {
  const own = connections.filter((connection) => connection.own !== false);
  const people = new Map<string, PrivateConnection[]>();
  for (const connection of connections.filter((item) => item.own === false)) {
    const person = connection.ownerName?.split(' ')[0] ?? 'A colleague';
    people.set(person, [...(people.get(person) ?? []), connection]);
  }
  return {
    own: own.length ? connectionNames(own) : null,
    others: [...people].map(([person, items]) => `${person}’s ${connectionNames(items)}`),
  };
}

/** "Gmail" or "Gmail and Slack": the names a Share warning lists. */
export function connectionNames(connections: PrivateConnection[]) {
  const names = [
    ...new Set(connections.map((connection) => connection.name ?? connection.provider ?? '')),
  ].filter(Boolean);
  if (names.length <= 1) return names[0] ?? 'private accounts';
  return `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`;
}

// The owner pressed Ask on a suggested follow-up: its text is in the
// composer, tagged "Suggested by …", until the owner sends it (the transport
// takes it once, as `suggestionId`) or drops the tag. Nothing sends by itself.
export interface AskedSuggestion {
  id: string;
  name: string | null;
}
const asked = new Map<string, AskedSuggestion>();
// Suggestions whose turn this tab already sent, per chat. The chip list is not
// reloaded after a turn, so without this an asked chip would come back with
// Ask enabled and could be asked twice. Each change stores a new Set, so a
// snapshot is stable until it changes.
const sent = new Map<string, ReadonlySet<string>>();
const NONE_SENT: ReadonlySet<string> = new Set();
const askedListeners = new Set<() => void>();
const changed = () => askedListeners.forEach((listener) => listener());

export function setAskedSuggestion(threadId: string, suggestion: AskedSuggestion | null) {
  if (suggestion) asked.set(threadId, suggestion);
  else asked.delete(threadId);
  changed();
}
export const readAskedSuggestion = (threadId: string) => asked.get(threadId) ?? null;
export const readSentSuggestions = (threadId: string) => sent.get(threadId) ?? NONE_SENT;
/** Read and clear: called once, when the owner's turn is sent. The id stays
 * hidden in this tab from then on. */
export function takeAskedSuggestion(threadId: string): AskedSuggestion | null {
  const suggestion = asked.get(threadId) ?? null;
  if (suggestion) {
    asked.delete(threadId);
    sent.set(threadId, new Set([...readSentSuggestions(threadId), suggestion.id]));
    changed();
  }
  return suggestion;
}
/** The body fields the owner's next turn carries: `suggestionId` once, for
 * the suggestion she asked, then nothing. The chat transport's extraBody. */
export function askedSuggestionBody(threadId: string): { suggestionId: string } | null {
  const suggestion = takeAskedSuggestion(threadId);
  return suggestion ? { suggestionId: suggestion.id } : null;
}
export function subscribeAskedSuggestions(listener: () => void) {
  askedListeners.add(listener);
  return () => askedListeners.delete(listener);
}
export function resetAskedSuggestionsForTests() {
  asked.clear();
  sent.clear();
  changed();
}
